import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { SecurityContext, Pipe, Injectable, EventEmitter, ElementRef, Component, ChangeDetectionStrategy, Input, Output, ViewChild, NgModule } from '@angular/core';
import anchorme from 'anchorme';
import * as i1 from '@angular/platform-browser';
import { Subject, fromEvent, of, merge, Subscription } from 'rxjs';
import { takeUntil, filter, throttleTime, distinctUntilChanged, map } from 'rxjs/operators';
import emojiRegex from 'emoji-regex';
import * as Sentry from '@sentry/capacitor';
import { Client } from '@twilio/conversations';
import * as zChat from 'zendesk-chat-sdk';

class AnchormePipe {
    constructor(domSanitizer) {
        this.domSanitizer = domSanitizer;
    }
    transform(value, args) {
        const text = anchorme({
            input: this.domSanitizer.sanitize(SecurityContext.HTML, value),
            options: {
                attributes: {
                    target: '_system'
                }
            }
        });
        return this.domSanitizer.bypassSecurityTrustHtml(text);
    }
}
AnchormePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: AnchormePipe, deps: [{ token: i1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe });
AnchormePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.1.3", ngImport: i0, type: AnchormePipe, name: "anchorme" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: AnchormePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'anchorme'
                }]
        }], ctorParameters: function () { return [{ type: i1.DomSanitizer }]; } });

var ChatServiceEvent;
(function (ChatServiceEvent) {
    // Events relating to our attempt to connect the client
    ChatServiceEvent["ConnectFailAuthError"] = "connect-fail-auth-error";
    ChatServiceEvent["ConnectFailNoToken"] = "connect-fail-no-token";
    ChatServiceEvent["ConnectNew"] = "connect-new";
    ChatServiceEvent["ConnectTimeout"] = "timeout";
    // Events relating to our attempts to disconnect the client
    ChatServiceEvent["DisconnectAttemptNoClient"] = "disconnect-attempt-no-client";
    ChatServiceEvent["DisconnectCompleted"] = "disconnect-completed";
    ChatServiceEvent["DisconnectInitiated"] = "disconnect-initiated";
    // Events indicating connection state changes in the client
    ChatServiceEvent["StateChangeConnected"] = "state-change-connected";
    ChatServiceEvent["StateChangeConnecting"] = "state-change-connecting";
    ChatServiceEvent["StateChangeDenied"] = "state-change-denied";
    ChatServiceEvent["StateChangeDisconnected"] = "state-change-disconnected";
    ChatServiceEvent["StateChangeDisconnecting"] = "state-change-disconnecting";
})(ChatServiceEvent || (ChatServiceEvent = {}));
class ChatService {
}

var Level;
(function (Level) {
    Level[Level["Debug"] = 0] = "Debug";
    Level[Level["Info"] = 1] = "Info";
    Level[Level["Warn"] = 3] = "Warn";
    Level[Level["Error"] = 4] = "Error";
})(Level || (Level = {}));
class ConsoleLogger {
    constructor(level, context) {
        this.level = level;
        this.context = context;
    }
    debug(message) {
        if (this.level <= Level.Debug) {
            window.console.debug(this.prependContext(message));
        }
    }
    info(message) {
        if (this.level <= Level.Info) {
            window.console.info(this.prependContext(message));
        }
    }
    warn(message) {
        if (this.level <= Level.Warn) {
            window.console.warn(this.prependContext(message));
        }
    }
    error(message) {
        if (this.level <= Level.Error) {
            window.console.error(this.prependContext(message));
        }
    }
    prependContext(message) {
        return `${this.context}: ${message}`;
    }
}

const EMOJI_REGEX = emojiRegex();
function exactMatchIgnoringEmojiVariation(string1, string2) {
    return string1.replace(/[\ufe00-\ufe0f]$/, '') === string2.replace(/[\ufe00-\ufe0f]$/, '');
}
class EmojifyService {
    constructor() {
        this.EMOJI_PATTERNS = new Map([
            [new RegExp(':-?\\)'), '😃'],
            [new RegExp(':-?D'), '😀'],
            [new RegExp(':-?\\('), '🙁'],
            [new RegExp(':-?\\|'), '😐'],
            [new RegExp(':-?[Oo]'), '😮'],
            [new RegExp(':-?[Pp]'), '😋'],
            [new RegExp(':\\+1'), '👍'],
            [new RegExp(':-1'), '👎']
        ]);
    }
    /**
     * Returns true if the given string contains exactly one emoji.
     * This is done by finding an emoji in the string using the `emoji-regex` package,
     * and returning true if there is exactly one match and that match is the entire input string
     * (ignoring any emoji variation selectors, which are not included in the regex match).
     * @param message
     */
    isSingleEmoji(message) {
        const matches = message.match(EMOJI_REGEX);
        return matches?.length === 1 && exactMatchIgnoringEmojiVariation(matches[0], message);
    }
    transform(message) {
        if (message.indexOf(':') === -1) {
            return message;
        }
        this.EMOJI_PATTERNS.forEach((emoji, pattern) => {
            message = message.replace(pattern, emoji);
        });
        return message;
    }
}
EmojifyService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: EmojifyService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
EmojifyService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: EmojifyService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: EmojifyService, decorators: [{
            type: Injectable
        }] });

class SentryService {
    /**
     * Capture a message using Sentry.
     */
    log({ message, level = 'debug', extras = null, tags = null, shouldLogSentry = false }) {
        // If sentry logging feature is not on, return early
        if (!shouldLogSentry) {
            return;
        }
        Sentry.withScope((scope) => {
            if (extras) {
                scope.setExtras(extras);
            }
            if (tags) {
                scope.setTags(tags);
            }
            Sentry.captureMessage(message, level);
        });
    }
}
SentryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: SentryService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SentryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: SentryService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: SentryService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

const FIRST_MESSAGE_INDEX = 0;
const GO_TO_NEWEST = 'newest';
const GO_TO_OLDEST = 'oldest';
var ErrorCode;
(function (ErrorCode) {
    ErrorCode[ErrorCode["NOT_FOUND"] = 0] = "NOT_FOUND";
    ErrorCode[ErrorCode["REJECTED"] = 1] = "REJECTED";
    ErrorCode[ErrorCode["OTHER"] = 2] = "OTHER";
    ErrorCode[ErrorCode["FAILED_TO_SEND"] = 3] = "FAILED_TO_SEND";
})(ErrorCode || (ErrorCode = {}));
var ErrorMessage;
(function (ErrorMessage) {
    ErrorMessage["DEFAULT"] = "Something went wrong with chat";
    ErrorMessage["ERROR_FETCHING_MESSAGES"] = "We encountered an error fetching messages. Tap here to try again.";
    ErrorMessage["ERROR_FETCHING_PREVIOUS"] = "We encountered an error fetching the previous messages. Tap here to try again.";
    ErrorMessage["OFFLINE"] = "You are offline. You can send and receive messages after connecting to the internet.";
    ErrorMessage["OFFLINE_DISCONNECTED"] = "The connection to the chat was lost. Please check your internet connection then tap here to reconnect.";
    ErrorMessage["OFFLINE_RECONNECTING"] = "The connection to the chat was lost: you will be automatically reconnected...";
    ErrorMessage["UNKNOWN_CONNECTION_ERROR"] = "Something went wrong connecting to the conversation. Tap here to try again.";
})(ErrorMessage || (ErrorMessage = {}));
// An error response with status 0 usually means the device is offline
const HTTP_STATUS_NO_INTERNET = 0;
// When offline, attempt to automatically reconnect this many times before giving up
const RECONNECTION_LIMIT = 30;
class HkChatClientComponentLogger extends ConsoleLogger {
    constructor() {
        super(Level.Error, 'HkChatClientComponent');
    }
}
HkChatClientComponentLogger.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: HkChatClientComponentLogger, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
HkChatClientComponentLogger.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: HkChatClientComponentLogger });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: HkChatClientComponentLogger, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });
class HkChatClientComponent {
    constructor(changeDetectorRef, emojify, logger, sentryService) {
        this.changeDetectorRef = changeDetectorRef;
        this.emojify = emojify;
        this.logger = logger;
        this.sentryService = sentryService;
        /** If false, the user cannot send new chat message */
        this.canSendNewMessages = true;
        /** Optionally, the chat initiator can "tag" the chat */
        this.chatTags = [];
        /** If true, older messages will still be loaded but will not be shown until "Get older messages" is clicked */
        this.hideOlderMessagesByDefault = false;
        /** Should be set to true if the user is a customer */
        this.isAuthorCustomer = false;
        /** Should be set to true if the user is a worker */
        this.isAuthorWorker = false;
        /** To determine the role of the author in customer<>housekeeper chat for handling phone numbers */
        this.isCustomerToHousekeeperChat = false;
        /** If true, show a message to explain the device is offline and messages cannot be sent or received */
        this.offline = false;
        /** If true, messages cannot be sent, and received messages are not marked as read */
        this.readOnly = false;
        /** If true, show the actions button next to the message input */
        this.showActionsButton = false;
        /** To track when phone call prompt is shown */
        this.callPromptShown = new EventEmitter();
        /** Allow the component's parent to see errors from the chat client. */
        this.error = new EventEmitter();
        /** Allow the component's parent to see actions taken by the user. */
        this.openActions = new EventEmitter();
        /** Allow component's parent to see messages received by the user. */
        this.receivedMessages = new EventEmitter();
        /** Allow component's parent to see messages sent by the user. */
        this.sentMessages = new EventEmitter();
        this.OFFLINE_MESSAGE = ErrorMessage.OFFLINE;
        this.QUICK_REPLIES = ['Thanks', 'Sure', '💙'];
        this.errorMessage = ErrorMessage.DEFAULT;
        this.hasErrorOccurred = false;
        this.initComplete = false;
        this.otherParticipantIsTyping = false;
        this.sending = false;
        this.componentDestroyEvents = new Subject();
        this.conversationDisconnectEvents = new Subject();
        this.messages = new Map();
        this.hasHiddenOlderMessages = false;
        this.reconnectionCounter = 0;
    }
    set conversationIdentifier(identifier) {
        if (identifier !== null && identifier !== undefined) {
            if (!this.chatService) {
                this.sentryService.log({
                    message: 'Chat Error: Could not get sentry service',
                    level: 'error',
                    extras: { identifier: identifier },
                    shouldLogSentry: this.shouldLogSentry
                });
                throw Error('Cannot connect to conversation without chat service');
            }
            this.connectToConversation(identifier).then();
        }
    }
    get calleeRole() {
        if (this.isAuthorCustomer) {
            return 'Housekeeper';
        }
        if (this.isAuthorWorker) {
            return 'Customer';
        }
        return undefined;
    }
    get errorMessageClass() {
        const cssClass = this.errorMessage === ErrorMessage.OFFLINE_RECONNECTING ? 'offline-message' : 'error-message';
        return this.errorAction ? `${cssClass} tappable` : cssClass;
    }
    get logoSource() {
        if (this.logoSrc) {
            return this.logoSrc;
        }
        return 'assets/img/housekeep-logo.png';
    }
    get messagesArray() {
        return Array.from(this.messages.values());
    }
    /**
     * State whether we have received the earliest message sent in this conversation.
     */
    atBeginningOfChat() {
        if (this.messages.size) {
            return this.messages.values().next().value.index === FIRST_MESSAGE_INDEX;
        }
        return this.initComplete;
    }
    canGetOlderMessages() {
        return this.initComplete && !this.offline && (!this.atBeginningOfChat() || this.hasHiddenOlderMessages);
    }
    clickActionsButton() {
        this.openActions.emit();
    }
    getLocationFromMessage(message) {
        if (message.attributes && message.attributes.location) {
            return message.attributes.location;
        }
        /*
         * For conversations that don't support message attributes, location is
         * sent as a Google maps link in the message body. This regex extracts the
         * latitude and longitude values from the link and returns a plain object
         * conforming to the `Location` interface.
         */
        const matches = message.body.match(/https:\/\/www\.google\.com\/maps\/search\/\?api=1&query=([0-9.\-]+),([0-9.\-]+)/);
        if (matches) {
            return {
                latitude: Number(matches[1]),
                longitude: Number(matches[2])
            };
        }
        return undefined;
    }
    /**
     * Request previous page of messages from ConversationService.
     *
     * @param goTo Should we go to the top or bottom of the message list afterwards?
     * @param setMessagesHidden Should the messages be loaded but hidden?
     */
    async getOlderMessages(goTo, setMessagesHidden = false) {
        this.logger.info(`getOlderMessages(${goTo})`);
        if (this.messages.size && this.atBeginningOfChat()) {
            if (this.hasHiddenOlderMessages) {
                this.messages.forEach(message => (message.hidden = false));
                this.hasHiddenOlderMessages = false;
            }
            return;
        }
        const olderMessages = await this.conversationService.getOlderMessages();
        if (setMessagesHidden) {
            this.hasHiddenOlderMessages = true;
        }
        // Maps iterate in insertion order therefore older messages must be inserted before.
        const existingValues = Array.from(this.messages.values());
        this.messages.clear();
        olderMessages.forEach(message => this.messages.set(message.index, { ...message, hidden: setMessagesHidden }));
        existingValues.forEach(message => this.messages.set(message.index, { ...message, hidden: false }));
        this.changeDetectorRef.detectChanges();
        if (goTo === GO_TO_NEWEST) {
            this.showNewestMessage();
        }
        else if (goTo === GO_TO_OLDEST) {
            this.showOldestMessage();
        }
    }
    getOwnNumbersFromMessage(message) {
        if (message.attributes && message.attributes.ownPhoneNumbers) {
            return message.attributes.ownPhoneNumbers;
        }
        return undefined;
    }
    lastMessageIsFromOtherUser() {
        if (this.messagesArray.length === 0) {
            return false;
        }
        const lastAuthorId = this.messagesArray[this.messagesArray.length - 1].authorId;
        return lastAuthorId !== this.myAuthorId && lastAuthorId !== 'system';
    }
    mapHref(location) {
        if (!location) {
            return null;
        }
        return `https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`;
    }
    mapSrc(location, opts = {}) {
        if (!this.googleMapsApiKey) {
            throw Error('Cannot embed static map without Google Maps API key');
        }
        if (!location) {
            return null;
        }
        return ('https://maps.googleapis.com/maps/api/staticmap?' +
            `markers=${location.latitude},${location.longitude}` +
            `&zoom=${opts.zoom || 16}` +
            `&size=${opts.size || '600x400'}` +
            `&scale=${opts.scale || 2}` +
            `&key=${this.googleMapsApiKey}`);
    }
    messageIsEmoji(message) {
        return this.emojify.isSingleEmoji(message.body);
    }
    messageIsFromMe(message) {
        return message.authorId === this.myAuthorId;
    }
    messageIsFromSystem(message) {
        return message.authorId === 'system';
    }
    /**
     * Messages can be uniquely identified by their index.
     *
     * @param index
     * @param message
     */
    messageTrackBy(index, message) {
        return message.index;
    }
    /**
     * Set up keyboard interactions.
     */
    ngAfterContentInit() {
        this.logger.info('ngAfterContentInit()');
        this.updateCanSend();
        // Send messages when user hits "enter"
        fromEvent(this.messageInput.nativeElement, 'keydown')
            .pipe(takeUntil(this.componentDestroyEvents), filter((event) => event.key === 'Enter' && !event.shiftKey))
            .subscribe((event) => {
            event.preventDefault();
            if (this.conversationService) {
                this.submitMessage().then();
            }
        });
        // Watch for all keyups and send a throttled stream of typing reports
        fromEvent(this.messageInput.nativeElement, 'keyup')
            .pipe(takeUntil(this.componentDestroyEvents), throttleTime(2000))
            .subscribe(() => {
            if (this.conversationService && !this.readOnly) {
                this.conversationService.reportTyping();
            }
        });
        // Intercept fancy pastes
        fromEvent(this.messageInput.nativeElement, 'paste')
            .pipe(takeUntil(this.componentDestroyEvents))
            .subscribe((event) => {
            event.preventDefault();
            const plainText = event.clipboardData.getData('text/plain');
            document.execCommand('insertHTML', false, plainText);
        });
        /**
         * visualViewport excluded keyboard display from the calculation
         * Listen for viewport resize events and scroll to the bottom of the
         * message container
         */
        fromEvent(window.visualViewport, 'resize')
            .pipe(takeUntil(this.componentDestroyEvents))
            .subscribe((event) => {
            event.preventDefault();
            setTimeout(() => {
                this.showNewestMessage();
            }, 100);
        });
    }
    ngOnDestroy() {
        this.logger.info('ngOnDestroy()');
        this.dissociateFromConversation();
        this.componentDestroyEvents.next();
        this.componentDestroyEvents.complete();
        this.error.complete();
        this.receivedMessages.complete();
        this.sentMessages.complete();
        if (this.reconnectionTimeout) {
            clearTimeout(this.reconnectionTimeout);
        }
    }
    /**
     * Do a full reload of the page.
     * Although this is not the most graceful way of reloading, it guarantees that the chat client starts in a clean state
     * and re-initialises the connection to the chat service. On web, if the user has no Internet, they will see their
     * browser's "No Internet" page, which will make it obvious why they cannot chat.
     */
    reloadPage() {
        window.location.reload();
    }
    /**
     * Defines the sending logic, including timeouts and error handling.
     */
    async sendMessage(message, attributes = {}, clearMessageInput = true) {
        if (this.readOnly) {
            this.error.emit({
                code: ErrorCode.FAILED_TO_SEND,
                error: 'Cannot send message in read-only mode'
            });
            return;
        }
        if (this.sanitizeFunction) {
            message = this.sanitizeFunction(message);
        }
        this.sending = true;
        const timer = setTimeout(() => {
            this.error.emit({
                code: ErrorCode.FAILED_TO_SEND
            });
            this.afterSendAttempt();
        }, 5000);
        try {
            await this.conversationService.submitNewMessage(message, { ...this.everyMessageAttributes, ...attributes });
            if (clearMessageInput) {
                this.messageInput.nativeElement.innerText = '';
            }
            this.afterSendAttempt(timer);
            this.sentMessages.emit(message);
        }
        catch (reason) {
            this.error.emit({
                code: this.errorCode(reason),
                error: reason
            });
            this.afterSendAttempt(timer);
            this.sentryService.log({
                message: 'Chat Error: Could not send message',
                level: 'error',
                extras: { reason },
                shouldLogSentry: this.shouldLogSentry
            });
        }
    }
    /**
     * Send the given location as a message via the conversationService.
     */
    submitLocation(location) {
        if (!location || !this.canSendNewMessages || this.sending) {
            return;
        }
        let message = 'Current location';
        if (location.accuracy > 5) {
            message += ` (accurate to ${Math.round(location.accuracy)} metres)`;
        }
        // Zendesk chat doesn't support sending attributes with messages,
        // so instead, we append a Google Maps link to the message body.
        if (!this.conversationService.supportsMessageAttributes) {
            message += '\n' + this.mapHref(location);
        }
        this.sendMessage(message, { location }).then();
    }
    /**
     * Send whatever the user has typed as a message via the conversationService.
     */
    submitMessage() {
        if (!this.canSendNewMessages || this.sending) {
            return;
        }
        let currentMessage = this.messageInput.nativeElement.innerText.trim();
        if (currentMessage === '') {
            return;
        }
        currentMessage = this.emojify.transform(currentMessage);
        if (this.isCustomerToHousekeeperChat) {
            const ownPhoneNumbers = this.getOwnPhoneNumbers(currentMessage);
            if (ownPhoneNumbers.length) {
                currentMessage = this.obscurePhoneNumbers(ownPhoneNumbers, currentMessage);
                this.callPromptShown.emit();
                return this.sendMessage(currentMessage, { ownPhoneNumbers });
            }
        }
        return this.sendMessage(currentMessage);
    }
    /**
     * Common actions after a send attempt, whether or not it succeeds.
     */
    afterSendAttempt(timer) {
        if (timer) {
            clearTimeout(timer);
        }
        this.sending = false;
        this.changeDetectorRef.detectChanges();
        this.focusOnMessageInput();
    }
    associateWithConversationService(conversationService, loadOlderMessages = true, hideOlderMessages = false) {
        this.conversationService = conversationService;
        this.updateCanSend();
        this.getCurrentMessages()
            .then(() => {
            if (loadOlderMessages) {
                // Load most recent page of older messages
                this.getOlderMessages(GO_TO_NEWEST, hideOlderMessages)
                    .then(() => {
                    this.initComplete = true;
                    this.changeDetectorRef.detectChanges();
                })
                    .catch(error => {
                    this.canSendNewMessages = false;
                    this.hasErrorOccurred = true;
                    this.errorMessage = ErrorMessage.ERROR_FETCHING_PREVIOUS;
                    this.errorAction = this.reloadPage;
                    this.sentryService.log({
                        message: 'Chat Error: Could not get old messages',
                        level: 'error',
                        extras: { error },
                        shouldLogSentry: this.shouldLogSentry
                    });
                });
            }
        })
            .catch(error => {
            this.canSendNewMessages = false;
            this.hasErrorOccurred = true;
            this.errorMessage = ErrorMessage.ERROR_FETCHING_MESSAGES;
            this.errorAction = this.reloadPage;
            this.sentryService.log({
                message: 'Chat Error: Could not get current messages',
                level: 'error',
                extras: { error },
                shouldLogSentry: this.shouldLogSentry
            });
        });
        if (!this.readOnly) {
            // Assume that the user has now seen all recent messages.
            conversationService.reportAllMessagesRead();
            // Perform tagging
            if (this.chatTags.length) {
                conversationService.tagChat(this.chatTags);
            }
        }
        conversationService.newMessage.pipe(takeUntil(this.conversationDisconnectEvents)).subscribe((message) => {
            if (!this.readOnly) {
                this.conversationService.reportMessageRead(message);
            }
            this.messages.set(message.index, message);
            this.changeDetectorRef.detectChanges();
            setTimeout(() => {
                this.showNewestMessage();
            }, 100);
            if (message.authorId !== this.myAuthorId) {
                this.receivedMessages.emit(message);
            }
        });
        conversationService.otherParticipantTyping
            .pipe(takeUntil(this.conversationDisconnectEvents))
            .subscribe((isTyping) => {
            this.otherParticipantIsTyping = isTyping;
            this.changeDetectorRef.detectChanges();
            setTimeout(() => {
                this.showNewestMessage();
            }, 100);
        });
        // Subscribe to the observable of read statuses. When one comes in from
        // another participant, mark messages accordingly.
        conversationService.readStatuses
            .pipe(takeUntil(this.conversationDisconnectEvents), distinctUntilChanged(), filter((readStatus) => readStatus.authorId !== this.myAuthorId))
            .subscribe((readStatus) => {
            this.messages.forEach((message, index) => {
                message.readByOtherParticipants = index <= readStatus.lastReadIndex;
            });
            this.changeDetectorRef.detectChanges();
        });
    }
    /**
     * Set up the association with conversation from scratch, including callbacks.
     *
     * @param identifier the unique conversation identifier
     * @param clearMessages if true (the default), clear messages already displayed (this should be set to false when reconnecting)
     */
    async connectToConversation(identifier, clearMessages = true) {
        // If the host application knows it is offline, wait and try again
        if (this.offline) {
            this.reconnectionTimeout = setTimeout(async () => {
                await this.connectToConversation(identifier, clearMessages);
            }, 500);
            return;
        }
        this.dissociateFromConversation(clearMessages);
        // If a timeout occurs when connecting, try again
        const connectTimeoutSubscription = this.chatService.on(ChatServiceEvent.ConnectTimeout, async () => {
            connectTimeoutSubscription.unsubscribe();
            await this.connectToConversation(identifier, false);
        });
        try {
            const conversationService = await this.chatService.getConversationService(identifier);
            this.associateWithConversationService(conversationService, clearMessages, this.hideOlderMessagesByDefault);
            if (this.firstMessage) {
                this.sendMessage(this.firstMessage).then();
                // Reset firstMessage so that this message is not sent again.
                this.firstMessage = undefined;
            }
            // Populate message input with draft text, if any
            if (this.draftMessage) {
                this.messageInput.nativeElement.innerText = this.draftMessage;
            }
            // After a successful connection, clear the error state
            this.reconnectionCounter = 0;
            this.hasErrorOccurred = false;
            this.canSendNewMessages = true;
            this.updateCanSend();
        }
        catch (reason) {
            this.initComplete = true;
            this.hasErrorOccurred = true;
            this.canSendNewMessages = false;
            if (reason?.status !== HTTP_STATUS_NO_INTERNET) {
                this.errorMessage = ErrorMessage.UNKNOWN_CONNECTION_ERROR;
                this.errorAction = this.reloadPage;
                this.error.emit({
                    code: this.errorCode(reason),
                    error: reason
                });
                this.sentryService.log({
                    message: 'Chat Error: Could not connect to conversation',
                    level: 'error',
                    extras: { reason },
                    shouldLogSentry: this.shouldLogSentry
                });
            }
            else if (this.reconnectionCounter >= RECONNECTION_LIMIT) {
                this.errorMessage = ErrorMessage.OFFLINE_DISCONNECTED;
                this.errorAction = this.reloadPage;
            }
            else {
                this.errorMessage = ErrorMessage.OFFLINE_RECONNECTING;
                this.errorAction = null;
                this.reconnectionCounter++;
                this.reconnectionTimeout = setTimeout(async () => {
                    await this.connectToConversation(identifier, false);
                }, 2000);
            }
        }
    }
    /**
     * Completely dispose of the old conversation and messages.
     *
     * @param clearMessages if true (the default), clear messages already displayed
     */
    dissociateFromConversation(clearMessages = true) {
        this.logger.info('dissociateFromConversation()');
        this.conversationDisconnectEvents.next();
        if (clearMessages) {
            this.messages.clear();
        }
        if (this.conversationService) {
            this.conversationService.tearDown();
        }
        delete this.conversationService;
        this.updateCanSend();
    }
    errorCode(reason) {
        if (!reason) {
            return ErrorCode.OTHER;
        }
        let code = -1;
        if (reason.hasOwnProperty('status')) {
            code = reason.status;
        }
        else if (reason.hasOwnProperty('code')) {
            code = reason.code;
        }
        else {
            return ErrorCode.OTHER;
        }
        if (code === 404) {
            return ErrorCode.NOT_FOUND;
        }
        if (code === 50056) {
            return ErrorCode.REJECTED;
        }
        return ErrorCode.OTHER;
    }
    /**
     * Send the user's cursor to the message input.
     */
    focusOnMessageInput() {
        if (this.messageInput) {
            this.messageInput.nativeElement.focus();
        }
    }
    /**
     * Given an input string with a phone number, format it to E.164.
     */
    formatInputNumber(inputNumber) {
        // strip the number string of all characters, but digits and +
        let formattedNumber = inputNumber.replace(/[^+0-9]/g, '');
        // if a number starts with a country prefix and has a trunk code, remove the trunk code
        let indexOfSeven = formattedNumber.indexOf('7');
        const startsWithCountryPrefix = formattedNumber.startsWith('0044') || formattedNumber.startsWith('+44');
        const hasTrunkCode = formattedNumber[indexOfSeven - 1] === '0';
        if (startsWithCountryPrefix && hasTrunkCode) {
            formattedNumber = formattedNumber.slice(0, indexOfSeven - 1) + formattedNumber.slice(indexOfSeven);
            indexOfSeven = formattedNumber.indexOf('7');
        }
        // if a number does not start with +44, make it, to match with E.164 numbers on record
        if (!formattedNumber.startsWith('+44')) {
            formattedNumber = '+44' + formattedNumber.slice(indexOfSeven);
        }
        return formattedNumber;
    }
    async getCurrentMessages() {
        this.logger.info('getCurrentMessages()');
        const messages = await this.conversationService.getCurrentMessages();
        if (messages.length === 0) {
            return;
        }
        for (const message of messages) {
            this.messages.set(message.index, message);
        }
        this.changeDetectorRef.detectChanges();
    }
    /**
     * Check the message for occurrences of UK phone numbers. For each match, call formatInputNumber to format to E.164
     * and, if it matches the author's on-record number, add it to the ownPhoneNumbers array, which will be returned.
     */
    getOwnPhoneNumbers(message) {
        const re = /(((0 *0|\+) *4 *4 *(\(? *0 *\)?)?)|0) *7 *(\d *){9}([^0-9]|$)/g;
        const ownPhoneNumbers = [];
        try {
            // find all occurrences of phone numbers in the message
            const matches = message.match(re);
            if (matches) {
                matches.forEach(inputNumber => {
                    // While the input number's last character is not a number, keep stripping characters from the end, so that
                    // we replace only the phone number in the original message.
                    while (/[^0-9]$/.test(inputNumber)) {
                        inputNumber = inputNumber.slice(0, -1);
                    }
                    const numberObject = {
                        inputNumber,
                        formattedNumber: this.formatInputNumber(inputNumber)
                    };
                    // Only push if formatted number matches with on-record number, so that we don't display the system message
                    // when other people's numbers are shared
                    if (this.authorTelephoneNumbers.includes(numberObject.formattedNumber)) {
                        ownPhoneNumbers.push(numberObject);
                    }
                });
            }
        }
        catch (error) {
            this.error.emit({
                code: this.errorCode(error),
                error
            });
        }
        return ownPhoneNumbers;
    }
    /**
     * For each number in the message, obscure everything between 7 and last 2 digits,
     * e.g. 07555666777 becomes 07xxxxxxx77.
     */
    obscurePhoneNumbers(phoneNumbers, message) {
        phoneNumbers.forEach(numberObject => {
            // remove whitespace from inputNumber to omit it from obscuring
            const noWhitespaceInputNumber = numberObject.inputNumber.replace(/ /g, '');
            const indexOfSeven = noWhitespaceInputNumber.indexOf('7');
            const obscuredNumber = noWhitespaceInputNumber.slice(0, indexOfSeven + 1) +
                noWhitespaceInputNumber.slice(indexOfSeven + 1, -2).replace(/./g, 'x') +
                noWhitespaceInputNumber.slice(-2);
            message = message.replace(numberObject.inputNumber, obscuredNumber);
        });
        return message;
    }
    /**
     * Scroll message panel to newest message.
     */
    showNewestMessage() {
        const messagesContainerElement = this.messagesContainer.nativeElement;
        messagesContainerElement.scrollTop = messagesContainerElement.scrollHeight;
    }
    /**
     * Scroll message panel to oldest message.
     */
    showOldestMessage() {
        this.messagesContainer.nativeElement.scrollTop = 0;
    }
    updateCanSend() {
        const editable = this.conversationService && this.canSendNewMessages;
        this.messageInput.nativeElement.setAttribute('contenteditable', editable);
        this.logger.info(`updateCanSend() -> ${editable}`);
    }
}
HkChatClientComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: HkChatClientComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: EmojifyService }, { token: HkChatClientComponentLogger }, { token: SentryService }], target: i0.ɵɵFactoryTarget.Component });
HkChatClientComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.1.3", type: HkChatClientComponent, selector: "hk-chat-client", inputs: { authorTelephoneNumbers: "authorTelephoneNumbers", canSendNewMessages: "canSendNewMessages", chatService: "chatService", chatTags: "chatTags", draftMessage: "draftMessage", everyMessageAttributes: "everyMessageAttributes", firstMessage: "firstMessage", googleMapsApiKey: "googleMapsApiKey", hideOlderMessagesByDefault: "hideOlderMessagesByDefault", isAuthorCustomer: "isAuthorCustomer", isAuthorWorker: "isAuthorWorker", isCustomerToHousekeeperChat: "isCustomerToHousekeeperChat", logoSrc: "logoSrc", myAuthorId: "myAuthorId", offline: "offline", readOnly: "readOnly", sanitizeFunction: "sanitizeFunction", shouldLogSentry: "shouldLogSentry", showActionsButton: "showActionsButton", conversationIdentifier: "conversationIdentifier" }, outputs: { callPromptShown: "callPromptShown", error: "error", openActions: "openActions", receivedMessages: "receivedMessages", sentMessages: "sentMessages" }, viewQueries: [{ propertyName: "messageInput", first: true, predicate: ["messageInput"], descendants: true, read: ElementRef, static: true }, { propertyName: "messagesContainer", first: true, predicate: ["messagesContainer"], descendants: true, read: ElementRef, static: true }], ngImport: i0, template: "<div class=\"hk-chat\">\n  <div #messagesContainer class=\"hk-chat__messages\">\n    <div class=\"hk-chat__info--beginning\" *ngIf=\"atBeginningOfChat()\">\n      <small><em>Beginning of your chat</em></small>\n    </div>\n    <div class=\"hk-chat__info--previous\" *ngIf=\"canGetOlderMessages()\">\n      <button\n        class=\"hk-chat__get-previous\"\n        (click)=\"getOlderMessages('oldest')\"\n        [disabled]=\"hasErrorOccurred\"\n      >\n        <em>Get older messages</em>\n      </button>\n    </div>\n    <div class=\"center-align\" *ngIf=\"!initComplete && !hasErrorOccurred\">\n      <div class=\"spinner\"></div>\n    </div>\n    <ng-container *ngFor=\"let message of messagesArray; trackBy: messageTrackBy\">\n      <ng-container *ngIf=\"!message.hidden\">\n        <div\n          class=\"hk-chat__messages__message message fade-in\"\n          [class.message--emoji]=\"messageIsEmoji(message)\"\n          [class.message--from-me]=\"messageIsFromMe(message)\"\n          [class.message--from-system]=\"messageIsFromSystem(message)\"\n        >\n          <div class=\"message__body\">\n            <div class=\"message__body__system-logo\" *ngIf=\"messageIsFromSystem(message)\">\n              Automated message:\n            </div>\n            <div\n              [class.hk-blue-heart-emoji]=\"message.body === '\uD83D\uDC99'\"\n              [innerHTML]=\"message.body | anchorme\"\n            ></div>\n            <div\n              class=\"message__body__map\"\n              *ngIf=\"getLocationFromMessage(message) as location\"\n            >\n              <a [href]=\"mapHref(location)\" target=\"_system\">\n                <img [src]=\"mapSrc(location)\" alt=\"Current location\" />\n              </a>\n            </div>\n          </div>\n\n          <div class=\"message__metadata\">\n            <small class=\"message__metadata__item message__metadata__item--timestamp\">\n              {{ message.timestamp | date: 'd MMM H:mm' }}\n              <span *ngIf=\"messageIsFromMe(message)\">\n                <svg viewBox=\"0 0 5 5\" width=\"5px\" height=\"5px\">\n                  <circle cx=\"1.5\" cy=\"1.5\" r=\"1.5\" fill=\"currentColor\" />\n                </svg>\n              </span>\n            </small>\n\n            <small\n              class=\"message__metadata__item message__metadata__item--server-received\"\n              *ngIf=\"\n                messageIsFromMe(message) &&\n                message.receivedByServer &&\n                !message.readByOtherParticipants\n              \"\n            >\n              Delivered\n            </small>\n            <small\n              class=\"message__metadata__item message__metadata__item--others-read\"\n              *ngIf=\"messageIsFromMe(message) && message.readByOtherParticipants\"\n            >\n              Read\n            </small>\n          </div>\n        </div>\n\n        <div\n          class=\"hk-chat__messages__message message fade-in\"\n          *ngIf=\"getOwnNumbersFromMessage(message) && calleeRole\"\n        >\n          <div class=\"message--from-system__body\">\n            <div class=\"message__body__system-logo\">\n              <img src=\"{{ logoSource }}\" />\n            </div>\n            <div class=\"message__body\">\n              <span class=\"message__body__call-prompt\">\n                If you are trying to make a call, click the\n              </span>\n              <span class=\"hk-chat__controls__button-copy\">+</span>\n              <span class=\"message__body__call-prompt\">\n                button below and select \"Call {{ calleeRole }}\"\n              </span>\n            </div>\n          </div>\n        </div>\n      </ng-container>\n    </ng-container>\n\n    <div\n      class=\"message hk-chat__messages__info--other-typing\"\n      *ngIf=\"otherParticipantIsTyping\"\n    >\n      <div class=\"typing-indicator\">\n        <span></span>\n        <span></span>\n        <span></span>\n      </div>\n    </div>\n  </div>\n\n  <!-- Offline div -->\n  <div *ngIf=\"offline\" class=\"offline-message\">\n    <p class=\"center-align\">{{ OFFLINE_MESSAGE }}</p>\n  </div>\n\n  <!-- Error div -->\n  <div\n    *ngIf=\"!offline && hasErrorOccurred\"\n    [class]=\"errorMessageClass\"\n    (click)=\"errorAction ? errorAction() : null\"\n  >\n    <p class=\"center-align\">{{ errorMessage }}</p>\n  </div>\n\n  <div\n    class=\"hk-chat__quick-replies\"\n    *ngIf=\"\n      !offline &&\n      !hasErrorOccurred &&\n      !sending &&\n      canSendNewMessages &&\n      lastMessageIsFromOtherUser()\n    \"\n  >\n    <button\n      *ngFor=\"let quickReply of QUICK_REPLIES\"\n      class=\"hk-chat__quick-replies__button\"\n      (click)=\"sendMessage(quickReply, {}, false)\"\n    >\n      <span [class.hk-blue-heart-emoji]=\"quickReply === '\uD83D\uDC99'\">\n        {{ quickReply }}\n      </span>\n    </button>\n  </div>\n\n  <div class=\"hk-chat__controls\">\n    <div class=\"hk-chat__controls__actions\" *ngIf=\"showActionsButton\">\n      <button\n        class=\"hk-chat__controls__button\"\n        (click)=\"clickActionsButton()\"\n        [disabled]=\"offline || !canSendNewMessages\"\n      >\n        +\n      </button>\n    </div>\n    <div class=\"hk-chat__controls__text\">\n      <div\n        class=\"hk-chat__controls__text__input\"\n        [class.hk-chat__controls__text__input--sending]=\"sending\"\n        #messageInput\n      ></div>\n    </div>\n    <div class=\"hk-chat__controls__submit\">\n      <button\n        class=\"hk-chat__controls__button\"\n        (click)=\"submitMessage()\"\n        [disabled]=\"offline || !canSendNewMessages\"\n      >\n        Send\n      </button>\n    </div>\n  </div>\n</div>\n", styles: ["[contenteditable]{-webkit-user-select:text;-moz-user-select:text;user-select:text}.hk-blue-heart-emoji{filter:hue-rotate(18deg) brightness(70%)}.hk-chat{font-family:Fabriga,-apple-system,BlinkMacSystemFont,Helvetica,Arial,sans-serif;font-weight:300;color:#000;width:100%;height:100%;display:flex;flex-direction:column;background:none;padding-top:5px}.hk-chat__get-previous{padding:5px;border-radius:2px;border:1px solid #ced7db}.hk-chat__messages{position:relative;display:flex;flex-direction:column;justify-content:flex-start;align-items:flex-start;flex-grow:1;flex-basis:200px;overflow-y:auto;border-radius:5px 5px 0 0;border:0 solid #ced7db;border-width:1px 1px 0px 1px;padding:5px}.hk-chat__controls{display:flex;flex-direction:row;align-items:center;background:#ced7db;padding:5px;border-radius:0 0 5px 5px}.hk-chat__controls__text{flex:1}.hk-chat__controls__text__input{padding:5px;background:#ffffff;border-radius:6px;outline:none;min-height:35px;overflow-wrap:break-word;word-wrap:break-word;word-break:break-word}.hk-chat__controls__text__input--sending{color:#8198a4;-webkit-user-modify:read-only;-moz-user-modify:read-only;user-modify:read-only}.hk-chat__controls__submit{padding-left:5px}.hk-chat__controls__actions{padding-right:5px}.hk-chat__controls__button{padding:0 8px;border:0 none;outline:none;line-height:35px;height:35px;min-width:35px;border-radius:6px;text-align:center;background:#3040b6;color:#fff;font-weight:500}.hk-chat__controls__button-copy{padding:.1rem .35rem;margin:0 .25rem;border-radius:.2rem;background:#3040b6;color:#fff;font-weight:500;font-size:.6rem;vertical-align:middle}.hk-chat__quick-replies{border-left:1px solid #ced7db;border-right:1px solid #ced7db;display:flex;justify-content:space-around}.hk-chat__quick-replies__button{background:#ffffff;border:1px solid #3040b6;border-radius:6px;color:#3040b6;flex-grow:1;font-weight:500;height:35px;line-height:35px;margin:2px;min-width:35px;outline:none;padding:0 8px;text-align:center}.message{padding:5px;margin-top:10px;max-width:80%;border-radius:0 6px 6px;border:1px solid #ced7db;background:#f3f5f7}.message--emoji{background:none!important;border:none!important}.message--emoji .message__body{font-size:60px;line-height:1.1;text-align:right}.message--from-me{align-self:flex-end;border-top-right-radius:0;border-top-left-radius:6px;background:#3040b6;color:#fff}.message--from-me ::ng-deep a{color:#eaecf8;text-decoration:underline}.message--from-me ::ng-deep a:hover{color:#d7daf1}.message--from-system{align-self:center;width:80%;border-top-left-radius:6px;background:#f3f5f7}.message--from-system ::ng-deep a{color:#3040b6;text-decoration:underline}.message--from-system ::ng-deep a:hover{color:#273493}.message__metadata{color:#8198a4;margin-top:4px}.message__body{overflow-wrap:break-word}.message__body__call-prompt{vertical-align:middle}.message__body__map{margin-top:4px;position:relative;padding-top:66.66667%}.message__body__map img{position:absolute;top:0;left:0;width:100%;height:auto}.message__body__system-logo{font-style:italic}.message__body__system-logo img{margin-bottom:-.15rem;height:1rem;width:1rem}.typing-indicator span{height:10px;width:10px;float:left;margin:0 1px;background-color:#9baeb7;display:block;border-radius:50%;opacity:.4}.typing-indicator span:nth-of-type(1){animation:1s blink infinite .3333s}.typing-indicator span:nth-of-type(2){animation:1s blink infinite .6666s}.typing-indicator span:nth-of-type(3){animation:1s blink infinite .9999s}.center-align{align-self:center}.error-message,.offline-message{align-self:center;width:100%;text-align:center}.error-message.tappable,.offline-message.tappable{cursor:pointer}.error-message{background:#fff5e6}.offline-message{background:#f3f5f7}.spinner{border-radius:50%;width:30px;height:30px;margin:20px auto;border-top:3px solid #f3f5f7;border-right:3px solid #f3f5f7;border-bottom:3px solid #f3f5f7;border-left:3px solid #3040b6;transform:translateZ(0);animation:spin 1s infinite linear}@keyframes spin{0%{transform:rotate(0)}to{transform:rotate(360deg)}}@keyframes blink{50%{opacity:1}}.fade-in{animation:fadein 1s}@keyframes fadein{0%{opacity:0}to{opacity:1}}\n"], dependencies: [{ kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i3.DatePipe, name: "date" }, { kind: "pipe", type: AnchormePipe, name: "anchorme" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: HkChatClientComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'hk-chat-client', template: "<div class=\"hk-chat\">\n  <div #messagesContainer class=\"hk-chat__messages\">\n    <div class=\"hk-chat__info--beginning\" *ngIf=\"atBeginningOfChat()\">\n      <small><em>Beginning of your chat</em></small>\n    </div>\n    <div class=\"hk-chat__info--previous\" *ngIf=\"canGetOlderMessages()\">\n      <button\n        class=\"hk-chat__get-previous\"\n        (click)=\"getOlderMessages('oldest')\"\n        [disabled]=\"hasErrorOccurred\"\n      >\n        <em>Get older messages</em>\n      </button>\n    </div>\n    <div class=\"center-align\" *ngIf=\"!initComplete && !hasErrorOccurred\">\n      <div class=\"spinner\"></div>\n    </div>\n    <ng-container *ngFor=\"let message of messagesArray; trackBy: messageTrackBy\">\n      <ng-container *ngIf=\"!message.hidden\">\n        <div\n          class=\"hk-chat__messages__message message fade-in\"\n          [class.message--emoji]=\"messageIsEmoji(message)\"\n          [class.message--from-me]=\"messageIsFromMe(message)\"\n          [class.message--from-system]=\"messageIsFromSystem(message)\"\n        >\n          <div class=\"message__body\">\n            <div class=\"message__body__system-logo\" *ngIf=\"messageIsFromSystem(message)\">\n              Automated message:\n            </div>\n            <div\n              [class.hk-blue-heart-emoji]=\"message.body === '\uD83D\uDC99'\"\n              [innerHTML]=\"message.body | anchorme\"\n            ></div>\n            <div\n              class=\"message__body__map\"\n              *ngIf=\"getLocationFromMessage(message) as location\"\n            >\n              <a [href]=\"mapHref(location)\" target=\"_system\">\n                <img [src]=\"mapSrc(location)\" alt=\"Current location\" />\n              </a>\n            </div>\n          </div>\n\n          <div class=\"message__metadata\">\n            <small class=\"message__metadata__item message__metadata__item--timestamp\">\n              {{ message.timestamp | date: 'd MMM H:mm' }}\n              <span *ngIf=\"messageIsFromMe(message)\">\n                <svg viewBox=\"0 0 5 5\" width=\"5px\" height=\"5px\">\n                  <circle cx=\"1.5\" cy=\"1.5\" r=\"1.5\" fill=\"currentColor\" />\n                </svg>\n              </span>\n            </small>\n\n            <small\n              class=\"message__metadata__item message__metadata__item--server-received\"\n              *ngIf=\"\n                messageIsFromMe(message) &&\n                message.receivedByServer &&\n                !message.readByOtherParticipants\n              \"\n            >\n              Delivered\n            </small>\n            <small\n              class=\"message__metadata__item message__metadata__item--others-read\"\n              *ngIf=\"messageIsFromMe(message) && message.readByOtherParticipants\"\n            >\n              Read\n            </small>\n          </div>\n        </div>\n\n        <div\n          class=\"hk-chat__messages__message message fade-in\"\n          *ngIf=\"getOwnNumbersFromMessage(message) && calleeRole\"\n        >\n          <div class=\"message--from-system__body\">\n            <div class=\"message__body__system-logo\">\n              <img src=\"{{ logoSource }}\" />\n            </div>\n            <div class=\"message__body\">\n              <span class=\"message__body__call-prompt\">\n                If you are trying to make a call, click the\n              </span>\n              <span class=\"hk-chat__controls__button-copy\">+</span>\n              <span class=\"message__body__call-prompt\">\n                button below and select \"Call {{ calleeRole }}\"\n              </span>\n            </div>\n          </div>\n        </div>\n      </ng-container>\n    </ng-container>\n\n    <div\n      class=\"message hk-chat__messages__info--other-typing\"\n      *ngIf=\"otherParticipantIsTyping\"\n    >\n      <div class=\"typing-indicator\">\n        <span></span>\n        <span></span>\n        <span></span>\n      </div>\n    </div>\n  </div>\n\n  <!-- Offline div -->\n  <div *ngIf=\"offline\" class=\"offline-message\">\n    <p class=\"center-align\">{{ OFFLINE_MESSAGE }}</p>\n  </div>\n\n  <!-- Error div -->\n  <div\n    *ngIf=\"!offline && hasErrorOccurred\"\n    [class]=\"errorMessageClass\"\n    (click)=\"errorAction ? errorAction() : null\"\n  >\n    <p class=\"center-align\">{{ errorMessage }}</p>\n  </div>\n\n  <div\n    class=\"hk-chat__quick-replies\"\n    *ngIf=\"\n      !offline &&\n      !hasErrorOccurred &&\n      !sending &&\n      canSendNewMessages &&\n      lastMessageIsFromOtherUser()\n    \"\n  >\n    <button\n      *ngFor=\"let quickReply of QUICK_REPLIES\"\n      class=\"hk-chat__quick-replies__button\"\n      (click)=\"sendMessage(quickReply, {}, false)\"\n    >\n      <span [class.hk-blue-heart-emoji]=\"quickReply === '\uD83D\uDC99'\">\n        {{ quickReply }}\n      </span>\n    </button>\n  </div>\n\n  <div class=\"hk-chat__controls\">\n    <div class=\"hk-chat__controls__actions\" *ngIf=\"showActionsButton\">\n      <button\n        class=\"hk-chat__controls__button\"\n        (click)=\"clickActionsButton()\"\n        [disabled]=\"offline || !canSendNewMessages\"\n      >\n        +\n      </button>\n    </div>\n    <div class=\"hk-chat__controls__text\">\n      <div\n        class=\"hk-chat__controls__text__input\"\n        [class.hk-chat__controls__text__input--sending]=\"sending\"\n        #messageInput\n      ></div>\n    </div>\n    <div class=\"hk-chat__controls__submit\">\n      <button\n        class=\"hk-chat__controls__button\"\n        (click)=\"submitMessage()\"\n        [disabled]=\"offline || !canSendNewMessages\"\n      >\n        Send\n      </button>\n    </div>\n  </div>\n</div>\n", styles: ["[contenteditable]{-webkit-user-select:text;-moz-user-select:text;user-select:text}.hk-blue-heart-emoji{filter:hue-rotate(18deg) brightness(70%)}.hk-chat{font-family:Fabriga,-apple-system,BlinkMacSystemFont,Helvetica,Arial,sans-serif;font-weight:300;color:#000;width:100%;height:100%;display:flex;flex-direction:column;background:none;padding-top:5px}.hk-chat__get-previous{padding:5px;border-radius:2px;border:1px solid #ced7db}.hk-chat__messages{position:relative;display:flex;flex-direction:column;justify-content:flex-start;align-items:flex-start;flex-grow:1;flex-basis:200px;overflow-y:auto;border-radius:5px 5px 0 0;border:0 solid #ced7db;border-width:1px 1px 0px 1px;padding:5px}.hk-chat__controls{display:flex;flex-direction:row;align-items:center;background:#ced7db;padding:5px;border-radius:0 0 5px 5px}.hk-chat__controls__text{flex:1}.hk-chat__controls__text__input{padding:5px;background:#ffffff;border-radius:6px;outline:none;min-height:35px;overflow-wrap:break-word;word-wrap:break-word;word-break:break-word}.hk-chat__controls__text__input--sending{color:#8198a4;-webkit-user-modify:read-only;-moz-user-modify:read-only;user-modify:read-only}.hk-chat__controls__submit{padding-left:5px}.hk-chat__controls__actions{padding-right:5px}.hk-chat__controls__button{padding:0 8px;border:0 none;outline:none;line-height:35px;height:35px;min-width:35px;border-radius:6px;text-align:center;background:#3040b6;color:#fff;font-weight:500}.hk-chat__controls__button-copy{padding:.1rem .35rem;margin:0 .25rem;border-radius:.2rem;background:#3040b6;color:#fff;font-weight:500;font-size:.6rem;vertical-align:middle}.hk-chat__quick-replies{border-left:1px solid #ced7db;border-right:1px solid #ced7db;display:flex;justify-content:space-around}.hk-chat__quick-replies__button{background:#ffffff;border:1px solid #3040b6;border-radius:6px;color:#3040b6;flex-grow:1;font-weight:500;height:35px;line-height:35px;margin:2px;min-width:35px;outline:none;padding:0 8px;text-align:center}.message{padding:5px;margin-top:10px;max-width:80%;border-radius:0 6px 6px;border:1px solid #ced7db;background:#f3f5f7}.message--emoji{background:none!important;border:none!important}.message--emoji .message__body{font-size:60px;line-height:1.1;text-align:right}.message--from-me{align-self:flex-end;border-top-right-radius:0;border-top-left-radius:6px;background:#3040b6;color:#fff}.message--from-me ::ng-deep a{color:#eaecf8;text-decoration:underline}.message--from-me ::ng-deep a:hover{color:#d7daf1}.message--from-system{align-self:center;width:80%;border-top-left-radius:6px;background:#f3f5f7}.message--from-system ::ng-deep a{color:#3040b6;text-decoration:underline}.message--from-system ::ng-deep a:hover{color:#273493}.message__metadata{color:#8198a4;margin-top:4px}.message__body{overflow-wrap:break-word}.message__body__call-prompt{vertical-align:middle}.message__body__map{margin-top:4px;position:relative;padding-top:66.66667%}.message__body__map img{position:absolute;top:0;left:0;width:100%;height:auto}.message__body__system-logo{font-style:italic}.message__body__system-logo img{margin-bottom:-.15rem;height:1rem;width:1rem}.typing-indicator span{height:10px;width:10px;float:left;margin:0 1px;background-color:#9baeb7;display:block;border-radius:50%;opacity:.4}.typing-indicator span:nth-of-type(1){animation:1s blink infinite .3333s}.typing-indicator span:nth-of-type(2){animation:1s blink infinite .6666s}.typing-indicator span:nth-of-type(3){animation:1s blink infinite .9999s}.center-align{align-self:center}.error-message,.offline-message{align-self:center;width:100%;text-align:center}.error-message.tappable,.offline-message.tappable{cursor:pointer}.error-message{background:#fff5e6}.offline-message{background:#f3f5f7}.spinner{border-radius:50%;width:30px;height:30px;margin:20px auto;border-top:3px solid #f3f5f7;border-right:3px solid #f3f5f7;border-bottom:3px solid #f3f5f7;border-left:3px solid #3040b6;transform:translateZ(0);animation:spin 1s infinite linear}@keyframes spin{0%{transform:rotate(0)}to{transform:rotate(360deg)}}@keyframes blink{50%{opacity:1}}.fade-in{animation:fadein 1s}@keyframes fadein{0%{opacity:0}to{opacity:1}}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: EmojifyService }, { type: HkChatClientComponentLogger }, { type: SentryService }]; }, propDecorators: { authorTelephoneNumbers: [{
                type: Input
            }], canSendNewMessages: [{
                type: Input
            }], chatService: [{
                type: Input
            }], chatTags: [{
                type: Input
            }], draftMessage: [{
                type: Input
            }], everyMessageAttributes: [{
                type: Input
            }], firstMessage: [{
                type: Input
            }], googleMapsApiKey: [{
                type: Input
            }], hideOlderMessagesByDefault: [{
                type: Input
            }], isAuthorCustomer: [{
                type: Input
            }], isAuthorWorker: [{
                type: Input
            }], isCustomerToHousekeeperChat: [{
                type: Input
            }], logoSrc: [{
                type: Input
            }], myAuthorId: [{
                type: Input
            }], offline: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], sanitizeFunction: [{
                type: Input
            }], shouldLogSentry: [{
                type: Input
            }], showActionsButton: [{
                type: Input
            }], conversationIdentifier: [{
                type: Input
            }], callPromptShown: [{
                type: Output
            }], error: [{
                type: Output
            }], openActions: [{
                type: Output
            }], receivedMessages: [{
                type: Output
            }], sentMessages: [{
                type: Output
            }], messageInput: [{
                type: ViewChild,
                args: ['messageInput', { read: ElementRef, static: true }]
            }], messagesContainer: [{
                type: ViewChild,
                args: ['messagesContainer', { read: ElementRef, static: true }]
            }] } });

class ConversationService {
    constructor() {
        this.displayName = '';
        this.newMessage = new Subject();
        this.otherParticipantTyping = new Subject();
        this.readStatuses = new Subject();
        this.supportsMessageAttributes = false;
    }
}
ConversationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: ConversationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ConversationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: ConversationService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: ConversationService, decorators: [{
            type: Injectable
        }] });

const PAGE_SIZE = 30;
class TwilioConversationServiceLogger extends ConsoleLogger {
    constructor() {
        super(Level.Error, 'TwilioConversationService');
    }
}
TwilioConversationServiceLogger.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: TwilioConversationServiceLogger, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
TwilioConversationServiceLogger.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: TwilioConversationServiceLogger });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: TwilioConversationServiceLogger, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });
class TwilioConversationService extends ConversationService {
    constructor(conversation, loggedInUser, logger) {
        super();
        this.conversation = conversation;
        this.loggedInUser = loggedInUser;
        this.logger = logger;
        this.supportsMessageAttributes = true;
        // Map of participants identity to the index of that participants's most recently
        // read message. This is used to answer the question: "Have all other
        // participants of this conversation read my message?"
        this.latestReadIndexes = new Map();
        // Store references to listener functions so we can explicitly unsubscribe later;
        this.messageAddedListener = (message) => this.newMessageReceived(message);
        this.participantUpdatedListener = event => this.participantUpdated(event.participant);
        this.typingEndedListener = () => this.otherParticipantTyping.next(false);
        this.typingStartedListener = () => this.otherParticipantTyping.next(true);
        this.displayName = conversation.friendlyName;
        // Respond to conversation events using our observables.
        conversation.on('messageAdded', this.messageAddedListener);
        conversation.on('typingStarted', this.typingStartedListener);
        conversation.on('typingEnded', this.typingEndedListener);
        // Get initial state of all participants
        this.getInitialParticipants(conversation).then();
        // Get notified of subsequent participant state updates.
        conversation.on('participantUpdated', this.participantUpdatedListener);
    }
    getCurrentMessages() {
        return Promise.resolve([]);
    }
    async getOlderMessages(newestMessageIndex) {
        this.logger.info(`getOlderMessages(${newestMessageIndex})`);
        if (!this.currentPaginator) {
            this.currentPaginator = await this.conversation.getMessages(PAGE_SIZE, newestMessageIndex);
        }
        else {
            this.currentPaginator = await this.currentPaginator.prevPage();
        }
        return this.currentPaginator.items.map((message) => this.messageFromTwilioMessage(message));
    }
    reportAllMessagesRead() {
        this.conversation.setAllMessagesRead();
    }
    reportMessageRead(message) {
        this.conversation.updateLastReadMessageIndex(message.index);
    }
    reportTyping() {
        // Tell Twilio we are typing. We do not have to send a subsequent stopped
        // typing event. The conversation service sends this automatically
        // after a few seconds unless this has been called again in the meantime.
        this.conversation.typing();
    }
    submitNewMessage(body, attributes) {
        return this.conversation.sendMessage(body, attributes);
    }
    tagChat(tags) {
        return Promise.resolve();
    }
    tearDown() {
        this.logger.info('tearDown()');
        // Instances of TwilioConversationService are short-lived and not reused, but
        // Twilio's conversation objects are longer-lived. Therefore, we are careful to
        // specifically remove the listeners that we set up during this service's
        // lifetime but do not call `removeAllListeners()`. This could remove
        // listeners that other actors are depending on.
        this.conversation.removeListener('messageAdded', this.messageAddedListener);
        this.conversation.removeListener('typingStarted', this.typingStartedListener);
        this.conversation.removeListener('typingEnded', this.typingEndedListener);
        this.conversation.removeListener('participantUpdated', this.participantUpdatedListener);
    }
    async getInitialParticipants(conversation) {
        const participants = await conversation.getParticipants();
        for (const participant of participants) {
            this.participantUpdated(participant);
        }
    }
    isReadByOtherParticipants(twilioMessage) {
        if (this.latestReadIndexes.size === 0) {
            return false;
        }
        let isRead = true;
        this.latestReadIndexes.forEach((lastReadIndex, authorId) => {
            if (authorId === twilioMessage.author) {
                // This is the author, so whether they have read is irrelevant.
                return;
            }
            if (lastReadIndex === null || lastReadIndex < twilioMessage.index) {
                // There is some non-author who has not seen.
                isRead = false;
            }
        });
        return isRead;
    }
    messageFromTwilioMessage(twilioMessage) {
        return {
            authorId: twilioMessage.author,
            body: twilioMessage.body,
            index: twilioMessage.index,
            timestamp: twilioMessage.dateCreated,
            uniqueId: twilioMessage.sid,
            attributes: twilioMessage.attributes,
            receivedByServer: true,
            readByOtherParticipants: this.isReadByOtherParticipants(twilioMessage)
        };
    }
    newMessageReceived(twilioMessage) {
        this.logger.info('newMessageReceived()');
        this.latestReadIndexes.set(this.loggedInUser.identity, twilioMessage.index);
        const message = this.messageFromTwilioMessage(twilioMessage);
        this.newMessage.next(message);
    }
    participantUpdated(participant) {
        this.logger.info(`participantUpdated(): ${participant.identity}`);
        this.latestReadIndexes.set(participant.identity, participant.lastReadMessageIndex);
        this.readStatuses.next({
            authorId: participant.identity,
            lastReadIndex: participant.lastReadMessageIndex
        });
    }
}

class AuthService {
}
class TwilioAuthService extends AuthService {
}
class ZendeskAuthService extends AuthService {
}

class Unread {
}
class UnreadTrackerService {
    constructor() {
        this.unreads = new Map();
    }
    get(uniqueName) {
        // If we don't know about this uniqueName, cannot make a statement
        if (!this.unreads.has(uniqueName)) {
            return undefined;
        }
        const unread = this.unreads.get(uniqueName);
        // If there is no last message assume no unreads
        if (unread.lastMessageIndex === null) {
            return 0;
        }
        // If there is no record of consumption, assume all messages unread,
        // defaulting to 0 if no messages.
        if (unread.lastReadMessageIndex === null) {
            return (unread.lastMessageIndex || 0) + 1;
        }
        return unread.lastMessageIndex - unread.lastReadMessageIndex;
    }
    getAll() {
        let total = 0;
        const conversationNames = Array.from(this.unreads.keys());
        for (const conversationUniqueName of conversationNames) {
            total += this.get(conversationUniqueName);
        }
        return total;
    }
    has(uniqueName) {
        return this.unreads.has(uniqueName);
    }
    reset() {
        this.unreads.clear();
    }
    set(uniqueName, lastReadMessageIndex, lastMessageIndex) {
        this.unreads.set(uniqueName, { lastReadMessageIndex: lastReadMessageIndex, lastMessageIndex });
    }
    setConversationLastMessageIndex(uniqueName, lastMessageIndex) {
        if (!this.unreads.has(uniqueName)) {
            throw new Error('Unknown Conversation');
        }
        this.unreads.get(uniqueName).lastMessageIndex = lastMessageIndex;
    }
    setConversationLastReadMessageIndex(uniqueName, lastReadMessageIndex) {
        if (!this.unreads.has(uniqueName)) {
            throw new Error('Unknown Conversation');
        }
        this.unreads.get(uniqueName).lastReadMessageIndex = lastReadMessageIndex;
    }
}
UnreadTrackerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: UnreadTrackerService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
UnreadTrackerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: UnreadTrackerService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: UnreadTrackerService, decorators: [{
            type: Injectable
        }] });

class TwilioChatServiceLogger extends ConsoleLogger {
    constructor() {
        super(Level.Error, 'TwilioChatService');
    }
}
TwilioChatServiceLogger.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: TwilioChatServiceLogger, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
TwilioChatServiceLogger.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: TwilioChatServiceLogger });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: TwilioChatServiceLogger, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });
class TwilioChatService extends ChatService {
    constructor(authService, logger, sentryService, twilioConversationServiceLogger, unreadTracker) {
        super();
        this.authService = authService;
        this.logger = logger;
        this.sentryService = sentryService;
        this.twilioConversationServiceLogger = twilioConversationServiceLogger;
        this.unreadTracker = unreadTracker;
        this.unreadUpdates = new Subject();
        this.chatServiceEvent = new Subject();
        this.clientPromise = null;
        this.connectionAttemptTimeout = null;
    }
    async connect() {
        this.logger.info('connect called');
        await this.getClient();
    }
    async disconnect() {
        this.logger.info('disconnect()');
        if (!this.clientPromise) {
            this.chatServiceEvent.next(ChatServiceEvent.DisconnectAttemptNoClient);
            return;
        }
        this.chatServiceEvent.next(ChatServiceEvent.DisconnectInitiated);
        const client = await this.clientPromise;
        client.removeAllListeners();
        await client.shutdown();
        this.clientPromise = null;
        this.chatServiceEvent.next(ChatServiceEvent.DisconnectCompleted);
    }
    /**
     * Run the supplied callback on every currently subscribed conversation
     *
     * Returns a promise that will resolve when all conversations have been processed.
     *
     * @param callback
     */
    async forEachSubscribedConversation(callback) {
        const client = await this.getClient();
        const paginator = await client.getSubscribedConversations();
        return this.recursiveForEachSubscribedConversation(paginator, callback);
    }
    getAuthService() {
        return this.authService;
    }
    async getClient() {
        if (!this.clientPromise) {
            this.logger.info('No existing clientPromise. Return new connection');
            return this.newConnection();
        }
        const client = await this.clientPromise;
        // Check connection state every time.
        this.logger.info(`Checked client state: ${client.connectionState}`);
        if (client.connectionState === 'connecting' || client.connectionState === 'connected') {
            return client;
        }
        if (client.connectionState === 'disconnecting' || client.connectionState === 'disconnected') {
            client.removeAllListeners();
            return this.newConnection();
        }
        if (client.connectionState === 'denied') {
            return this.updateToken(client);
        }
    }
    async getConversationService(identifier) {
        const conversation = await this.getConversation(identifier);
        this.logger.info(`got conversation service: ${identifier}`);
        return new TwilioConversationService(conversation, this.loggedInUser, this.twilioConversationServiceLogger);
    }
    /**
     * Give back an observable that will be told each time a new unread message arrives on any subscribed conversation
     */
    getUnreadMessagesAllSubscribedConversationsObservable() {
        const numUnread = this.getTotalUnreadMessagesForAllSubscribedConversations();
        const initialValueObs = of(numUnread);
        return merge(initialValueObs, this.unreadUpdates.pipe(map(() => this.unreadTracker.getAll())));
    }
    /**
     * Give back an observable that will be told each time a given conversation updates.
     *
     * @param desiredUniqueName unique identifier for conversation of interest
     */
    getUnreadMessagesObservable(desiredUniqueName) {
        const numUnread = this.numMessagesIHaveNotRead(desiredUniqueName);
        let initialValueObs;
        if (numUnread !== undefined) {
            initialValueObs = of(numUnread);
        }
        else {
            initialValueObs = of();
        }
        return merge(initialValueObs, this.unreadUpdates.pipe(filter((updatedUniqueName) => {
            return updatedUniqueName === desiredUniqueName && this.unreadTracker.has(desiredUniqueName);
        }), map((uniqueName) => this.unreadTracker.get(uniqueName))));
    }
    on(event, next) {
        const observable = this.chatServiceEvent.asObservable();
        if (next) {
            return observable.pipe(filter(e => e === event)).subscribe(next);
        }
        return observable.subscribe(event);
    }
    setAuthService(authService) {
        this.authService = authService;
    }
    conversationAdded(conversation) {
        this.logger.info(`conversationAdded(${conversation.friendlyName})`);
        // If conversation is new, will not have lastMessage set.
        const idx = !conversation.lastMessage ? null : conversation.lastMessage.index;
        this.unreadTracker.set(conversation.uniqueName, conversation.lastReadMessageIndex, idx);
        this.unreadUpdates.next(conversation.uniqueName);
    }
    conversationUpdated(conversation, updateReasons) {
        this.logger.info(`conversationUpdated(${conversation.friendlyName}, ${updateReasons})`);
        let updated = false;
        if (!this.unreadTracker.get(conversation.uniqueName) === undefined) {
            this.conversationAdded(conversation);
        }
        if (updateReasons.indexOf('lastReadMessageIndex') !== -1) {
            this.unreadTracker.setConversationLastReadMessageIndex(conversation.uniqueName, conversation.lastReadMessageIndex);
            updated = true;
        }
        if (updateReasons.indexOf('lastMessage') !== -1) {
            this.unreadTracker.setConversationLastMessageIndex(conversation.uniqueName, conversation.lastMessage.index);
            updated = true;
        }
        if (updated) {
            this.unreadUpdates.next(conversation.uniqueName);
        }
    }
    async getConversation(identifier) {
        const client = await this.getClient();
        return client.getConversationByUniqueName(identifier);
    }
    /**
     * Return the total number of unread messages across all subscribed conversation
     */
    getTotalUnreadMessagesForAllSubscribedConversations() {
        return this.unreadTracker.getAll();
    }
    newConnection() {
        this.logger.info('newConnection()');
        this.chatServiceEvent.next(ChatServiceEvent.ConnectNew);
        this.clientPromise = new Promise(async (resolve, reject) => {
            try {
                const token = await this.authService.getToken();
                if (!token) {
                    this.chatServiceEvent.next(ChatServiceEvent.ConnectFailNoToken);
                    reject(new Error('Could not get token'));
                }
                const client = new Client(token);
                this.loggedInUser = client.user;
                // Before giving back the client, we connect our standard event callbacks
                client.on('conversationUpdated', (event) => {
                    this.conversationUpdated(event.conversation, event.updateReasons);
                });
                client.on('conversationAdded', (conversation) => {
                    this.conversationAdded(conversation);
                });
                client.on('conversationRemoved', (conversation) => {
                    this.unreadTracker.set(conversation.uniqueName, 0, null);
                    this.unreadUpdates.next(conversation.uniqueName);
                });
                // We need to subscribe to the Twilio client's state change events
                // so that we can detect if it begins a reconnection attempt on
                // its own initiative. If so, We must add timeout logic, or it
                // gets stuck in the "connecting" state when there's no network.
                client.on('connectionStateChanged', (connectionState) => {
                    this.logger.info(`connectionStateChanged: ${connectionState}`);
                    // If starting a connection attempt, give up after 10s
                    if (connectionState === 'connecting') {
                        this.chatServiceEvent.next(ChatServiceEvent.StateChangeConnecting);
                        if (this.connectionAttemptTimeout) {
                            // Existing timeout; clear it for the new attempt.
                            window.clearTimeout(this.connectionAttemptTimeout);
                        }
                        this.connectionAttemptTimeout = window.setTimeout(async () => {
                            await this.disconnect();
                            this.chatServiceEvent.next(ChatServiceEvent.ConnectTimeout);
                        }, 10000);
                    }
                    // If connection successful, clear the "give up" timeout and resolve
                    if (connectionState === 'connected') {
                        this.chatServiceEvent.next(ChatServiceEvent.StateChangeConnected);
                        window.clearTimeout(this.connectionAttemptTimeout);
                        this.connectionAttemptTimeout = null;
                        resolve(client);
                    }
                    if (connectionState === 'disconnecting') {
                        this.chatServiceEvent.next(ChatServiceEvent.StateChangeDisconnecting);
                    }
                    if (connectionState === 'disconnected') {
                        this.chatServiceEvent.next(ChatServiceEvent.StateChangeDisconnected);
                        reject(new Error('Unexpected Twilio client disconnection'));
                    }
                    if (connectionState === 'denied') {
                        this.chatServiceEvent.next(ChatServiceEvent.StateChangeDenied);
                        reject(new Error('Unexpected Twilio client denied'));
                    }
                });
            }
            catch (error) {
                this.chatServiceEvent.next(ChatServiceEvent.ConnectFailAuthError);
                this.logger.error(`authService.getToken threw: ${error}`);
                // authService or Twilio connection has failed. Set clientPromise
                // to null so that future calls will reconnect.
                this.clientPromise = null;
                reject(error);
            }
        });
        return this.clientPromise;
    }
    /**
     * How many messages has logged in user not read on a conversation?
     *
     * @param uniqueName unique identifier for conversation
     */
    numMessagesIHaveNotRead(uniqueName) {
        return this.unreadTracker.get(uniqueName);
    }
    async recursiveForEachSubscribedConversation(paginator, callback) {
        paginator.items.forEach((conversation) => {
            callback(conversation);
        });
        if (paginator.hasNextPage) {
            const nextPagePaginator = await paginator.nextPage();
            return this.recursiveForEachSubscribedConversation(nextPagePaginator, callback);
        }
        else {
            return Promise.resolve();
        }
    }
    async updateToken(client) {
        this.logger.info('updateToken called');
        const token = await this.authService.getToken();
        return client.updateToken(token);
    }
}
TwilioChatService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: TwilioChatService, deps: [{ token: TwilioAuthService }, { token: TwilioChatServiceLogger }, { token: SentryService }, { token: TwilioConversationServiceLogger }, { token: UnreadTrackerService }], target: i0.ɵɵFactoryTarget.Injectable });
TwilioChatService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: TwilioChatService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: TwilioChatService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: TwilioAuthService }, { type: TwilioChatServiceLogger }, { type: SentryService }, { type: TwilioConversationServiceLogger }, { type: UnreadTrackerService }]; } });

const INITIAL_MESSAGE_INDEX = 1000;
const FRONT_OF_QUEUE = 0;
const SYSTEM_AUTHOR = 'system';
class ZendeskConversationService extends ConversationService {
    constructor() {
        super();
        this.historicMessageBatch = new Array(); // FIXME private
        this.fetchingOlderMessages = false;
        this.latestTypingTimer = null;
        /*
         * Zendesk doesn't know the idea of message indices, so we
         * keep our own markers for oldest and newest index to insert
         * new and historic messages.
         */
        this._newestMessageIndex = INITIAL_MESSAGE_INDEX;
        this._oldestMessageIndex = INITIAL_MESSAGE_INDEX - 1;
        /**
         * Zendesk reports several different events of interest via one
         * callback. This handler can deal with new messages, typing
         * updates, and queue position updates.
         */
        this.chatHandler = (eventData) => {
            if (eventData.type === 'chat.msg') {
                this.newMessage.next(this.createMessage(eventData, true));
            }
            else if (eventData.type === 'typing') {
                if (eventData.typing) {
                    this.otherParticipantTyping.next(true);
                }
                else {
                    this.otherParticipantTyping.next(false);
                }
            }
            else if (eventData.type === 'chat.queue_position') {
                this.queuePositionUpdate(eventData);
            }
            else if (eventData.type === 'chat.memberleave') {
                const message = this.userLeftTheChatMessage(eventData, true);
                if (message !== null) {
                    this.newMessage.next(message);
                }
            }
        };
        /**
         * We register this handler to take care of 'history' events. The
         * only history we're interested in is a historic message.
         */
        this.historicMessageHandler = (eventData) => {
            if (eventData.type === 'chat.msg') {
                this.historicMessageBatch.push(this.createMessage(eventData, false));
            }
            else if (eventData.type === 'chat.memberleave') {
                const message = this.userLeftTheChatMessage(eventData, false);
                if (message !== null) {
                    this.historicMessageBatch.push(message);
                }
            }
        };
        zChat.on('chat', this.chatHandler);
        zChat.on('history', this.historicMessageHandler);
    }
    get newestMessageIndex() {
        return this._newestMessageIndex++;
    }
    get oldestMessageIndex() {
        return this._oldestMessageIndex--;
    }
    /**
     * Zendesk has separate notions of getting the messages in the current chat,
     * versus getting messages in 'historic' chats.
     *
     * Here, we ask it for just the current messagaes.
     */
    getCurrentMessages() {
        const messages = new Array();
        zChat.getChatLog().forEach((eventData) => {
            if (eventData.type === 'chat.msg') {
                messages.push(this.createMessage(eventData, true));
            }
            if (eventData.type === 'chat.memberleave') {
                messages.push(this.userLeftTheChatMessage(eventData, true));
            }
        });
        return Promise.resolve(messages);
    }
    /**
     * We work with Zendesk's API for historic messages by:
     * - making the request for the chat history, giving a callback
     * - while we wait for the callback to be called, Zendesk will send
     *   events to other callback(s) registered for its "history" event
     * - After a batch of these events has been sent, Zendesk calls our
     *   passed-in callback
     *
     * As a result of this pattern, concurrent calls to this method are
     * prevented.
     *
     * The passed-in callback gets information about whether we have reached
     * the start of the message history.
     *
     * @param newestMessageIndex
     */
    getOlderMessages(newestMessageIndex) {
        // Concurrent calls to fetchChatHistory are forbidden
        if (this.fetchingOlderMessages) {
            return Promise.resolve([]);
        }
        this.fetchingOlderMessages = true;
        return new Promise((resolve, reject) => {
            zChat.fetchChatHistory((err, data) => {
                if (err) {
                    reject(err);
                }
                else {
                    if (!data.has_more && this.historicMessageBatch.length) {
                        // We have reached the first message.
                        this.historicMessageBatch[0].index = FIRST_MESSAGE_INDEX;
                    }
                    // historicMessageBatch should have been filled by event
                    // callbacks.
                    resolve(this.historicMessageBatch);
                    this.historicMessageBatch = new Array();
                }
                this.fetchingOlderMessages = false;
            });
        });
    }
    reportAllMessagesRead() {
        zChat.markAsRead();
    }
    reportMessageRead(message) {
        zChat.markAsRead();
    }
    /**
     * Zendesk demands explicity "typing" and "stopped typing" reports.
     * We state that typing has stopped after 3 seconds unless another
     * report has come in.
     */
    reportTyping() {
        if (this.latestTypingTimer) {
            clearTimeout(this.latestTypingTimer);
        }
        zChat.sendTyping(true);
        this.latestTypingTimer = setTimeout(() => {
            zChat.sendTyping(false);
            this.latestTypingTimer = null;
        }, 3000);
    }
    submitNewMessage(body, attributes) {
        return new Promise(resolve => {
            zChat.sendChatMsg(body, () => {
                const message = {
                    body: body,
                    authorId: 'visitor',
                    index: this.newestMessageIndex,
                    timestamp: new Date(),
                    receivedByServer: true,
                    readByOtherParticipants: true
                };
                this.newMessage.next(message);
                resolve();
            });
        });
    }
    tagChat(tags) {
        return new Promise((resolve, reject) => {
            zChat.addTags(tags, err => {
                if (err) {
                    reject();
                }
                else {
                    resolve();
                }
            });
        });
    }
    tearDown() {
        clearTimeout(this.latestTypingTimer);
        zChat.un('chat', this.chatHandler);
        zChat.un('history', this.historicMessageHandler);
    }
    createMessage(eventData, isNew) {
        if (eventData.type !== 'chat.msg') {
            throw Error('Only valid to pass chat.msg events');
        }
        return {
            authorId: eventData.nick === 'agent:trigger' ? SYSTEM_AUTHOR : eventData.nick,
            body: eventData.msg,
            index: isNew ? this.newestMessageIndex : this.oldestMessageIndex,
            timestamp: new Date(eventData.timestamp),
            receivedByServer: true,
            readByOtherParticipants: true
        };
    }
    queuePositionUpdate(eventData) {
        if (eventData.type !== 'chat.queue_position') {
            throw Error('Only valid to pass chat.queue_position events');
        }
        let body;
        if (eventData.queue_position === FRONT_OF_QUEUE) {
            body = 'Thanks for waiting. Connecting you...';
        }
        else {
            body =
                `You are at position ${eventData.queue_position} in the queue. ` +
                    `Feel free to describe your issue while you wait.`;
        }
        const message = {
            authorId: SYSTEM_AUTHOR,
            body: body,
            index: this.newestMessageIndex,
            timestamp: new Date()
        };
        this.newMessage.next(message);
    }
    userLeftTheChatMessage(eventData, isNew) {
        if (eventData.type !== 'chat.memberleave') {
            throw Error('Only valid to pass chat.memberleave events');
        }
        const whoLeftMessage = eventData.nick.startsWith('agent')
            ? 'The customer service agent has left the chat.'
            : 'You left the chat.';
        const body = `${whoLeftMessage} Send a message to start a new chat.`;
        return {
            authorId: SYSTEM_AUTHOR,
            body: body,
            index: isNew ? this.newestMessageIndex : this.oldestMessageIndex,
            timestamp: new Date()
        };
    }
}

/**
 * This is a lightweight service that can convert Zendesk 'chat.msg' events
 * into notifications on RxJS Subjects.
 */
class ZendeskReplyMonitor {
    constructor() {
        /**
         * Subject broadcast to whenever an agent leaves the chat.
         */
        this.agentLeft = new Subject();
        /**
         * Subject broadcast to whenever a new chat reply is received.
         */
        this.replyReceived = new Subject();
        this.chatEventHandler = (eventData) => {
            if (eventData.type === 'chat.msg') {
                this.replyReceived.next();
            }
            if (eventData.type === 'chat.memberleave') {
                this.agentLeft.next();
                this.tearDown();
            }
        };
        zChat.on('chat', this.chatEventHandler);
    }
    tearDown() {
        zChat.un('chat', this.chatEventHandler);
        this.replyReceived.complete();
        this.agentLeft.complete();
    }
}

/**
 * Zendesk Chat SDK's connection has internal state management. However
 * there is a period of time between calling `init()` and the connection's
 * state transitioning to `connecting`. During this time, the SDK sends for
 * the JWT token, a server roundtrip which can take seconds. Therefore, a
 * new request to connect during this period will not see that the state
 * is already `connecting` and may try to connect from scratch instead of
 * waiting for the existing connection attempt.
 *
 * To overcome this, we add our own layer of state management which
 * has a `connecting` state encompassing both the authorization call
 * and the SDK's establishment of a websocket connection.
 */
var ChatServiceState;
(function (ChatServiceState) {
    ChatServiceState[ChatServiceState["OFFLINE"] = 0] = "OFFLINE";
    ChatServiceState[ChatServiceState["CONNECTING"] = 1] = "CONNECTING";
    ChatServiceState[ChatServiceState["CONNECTED"] = 2] = "CONNECTED";
})(ChatServiceState || (ChatServiceState = {}));
class ZendeskChatService extends ChatService {
    constructor(authService) {
        super();
        this.authService = authService;
        this.unreadUpdates = new Subject();
        this.state = ChatServiceState.OFFLINE;
        this.zChatInitialised = false;
        // Monitor connection updates throughout the life of this service,
        // updating our own state as the SDK's state transitions.
        zChat.on('connection_update', (zendeskState) => {
            if (zendeskState === 'connecting') {
                this.state = ChatServiceState.CONNECTING;
            }
            if (zendeskState === 'connected') {
                this.state = ChatServiceState.CONNECTED;
            }
            if (zendeskState === 'closed') {
                this.state = ChatServiceState.OFFLINE;
            }
        });
    }
    async accountIsOnline() {
        await this.connect();
        return zChat.getAccountStatus() === 'online';
    }
    async chatIsOngoing() {
        await this.connect();
        return zChat.isChatting();
    }
    connect() {
        // If we're not offline, return the existing promise.
        if (this.state !== ChatServiceState.OFFLINE) {
            return this.connectedPromise;
        }
        // Explicitly modify the state ourselves to put it in connecting
        // state while we fetch the JWT token. Subsequent state changes
        // come via event callbacks set up above.
        this.state = ChatServiceState.CONNECTING;
        this.connectedPromise = new Promise((resolve, reject) => {
            this.zChatInitialised = true;
            zChat.init({
                account_key: this.authService.getAccountKey(),
                authentication: {
                    jwt_fn: async (callback) => {
                        try {
                            callback(await this.authService.getToken());
                        }
                        catch (error) {
                            this.state = ChatServiceState.OFFLINE;
                            reject(error.hasOwnProperty('status') && error.status === 403 ? 'Permission denied' : 'Authorisation failed');
                        }
                    }
                }
            });
            const handler = (zendeskState) => {
                if (zendeskState === 'connecting') {
                    return;
                }
                else if (zendeskState === 'connected') {
                    resolve();
                }
                else if (zendeskState === 'closed') {
                    reject();
                }
                // Stop listening for events after handling
                zChat.un('connection_update', handler);
            };
            // Listen for updates so that we can resolve the promise.
            zChat.on('connection_update', handler);
        });
        return this.connectedPromise;
    }
    disconnect() {
        // Transition our own state aggressively early to ensure
        // there's no window during which a new call to connect
        // could be told there is an active connection
        this.state = ChatServiceState.OFFLINE;
        if (this.zChatInitialised) {
            zChat.logout();
        }
    }
    endChat() {
        return new Promise((resolve, reject) => {
            zChat.endChat(err => {
                if (err) {
                    reject(err);
                }
                else {
                    this.disconnect();
                    resolve();
                }
            });
        });
    }
    getAuthService() {
        return this.authService;
    }
    async getConversationService(identifier) {
        await this.connect();
        return new ZendeskConversationService();
    }
    async getReplyMonitor() {
        await this.connect();
        return new ZendeskReplyMonitor();
    }
    on(event, next) {
        return new Subscription();
    }
    setAuthService(authService) {
        this.authService = authService;
    }
}
ZendeskChatService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: ZendeskChatService, deps: [{ token: ZendeskAuthService }], target: i0.ɵɵFactoryTarget.Injectable });
ZendeskChatService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: ZendeskChatService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: ZendeskChatService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: ZendeskAuthService }]; } });

class HkChatClientModule {
}
HkChatClientModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: HkChatClientModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
HkChatClientModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.1.3", ngImport: i0, type: HkChatClientModule, declarations: [HkChatClientComponent, AnchormePipe], imports: [CommonModule], exports: [HkChatClientComponent] });
HkChatClientModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: HkChatClientModule, providers: [
        EmojifyService,
        HkChatClientComponentLogger,
        TwilioChatService,
        TwilioChatServiceLogger,
        TwilioConversationServiceLogger,
        UnreadTrackerService,
        ZendeskChatService
    ], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: HkChatClientModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [HkChatClientComponent, AnchormePipe],
                    exports: [HkChatClientComponent],
                    providers: [
                        EmojifyService,
                        HkChatClientComponentLogger,
                        TwilioChatService,
                        TwilioChatServiceLogger,
                        TwilioConversationServiceLogger,
                        UnreadTrackerService,
                        ZendeskChatService
                    ]
                }]
        }] });

class Message {
}

class ParticipantReadStatus {
}

/**
 * Generated bundle index. Do not edit.
 */

export { AnchormePipe, AuthService, ChatService, ChatServiceEvent, ConsoleLogger, ConversationService, EmojifyService, ErrorCode, FIRST_MESSAGE_INDEX, HkChatClientComponent, HkChatClientComponentLogger, HkChatClientModule, Level, Message, ParticipantReadStatus, TwilioAuthService, TwilioChatService, TwilioChatServiceLogger, TwilioConversationService, TwilioConversationServiceLogger, UnreadTrackerService, ZendeskAuthService, ZendeskChatService, ZendeskConversationService, ZendeskReplyMonitor };
